/* eslint-disable sonarjs/no-duplicate-string, sonarjs/cognitive-complexity */
import React, { useState, useMemo, useCallback, useEffect, useRef, type JSX } from 'react'
import { Controller } from 'react-hook-form'
import { useRouter } from 'next/router'
import { differenceInDays } from 'date-fns'
import classNames from 'classnames'
import {
  AutoCompleteLocationOption,
  AutoCompleteWithMobileModal,
  DatePicker,
} from '@reward-platform/lift/components'
import { createAutoCompleteLocationSource } from '~/utils/createAutoCompleteSourceFromLocation'
import { CarHireSearchFormParams } from '@reward-platform/ancillaries-schemas/car-hire'
import { useQueryParams } from '~/hooks/useQueryParams/useQueryParams'
import { useLocalisation } from '~/context/localisation'
import { LocationOptionProps } from '@reward-platform/lift/components/AutoComplete/components/AutoComplete.types'
import { getDateTime } from '@reward-platform/lift/utils/datetime'
import {
  CarHireLocation,
  getCommonBrandIds,
  getServingOpeningTimes,
  isClosedOnDate,
} from '@reward-platform/ancillaries-locations'
import { Box, Button, Checkbox, TextField } from '@alto-avios/alto-ui'
import { useMediaQuery } from 'usehooks-ts'
import {
  gtmSearchSubmit,
  refineSearchSessionStorage,
} from '~/utils/googleTagManager/googleTagManagerHelperFunctions'
import { deviceSizes } from '@reward-platform/lift/theme/deviceSizes'
import { Tooltip } from '@reward-platform/lift/components/Tooltip/Tooltip'
import { useFeatureFlag } from '~/context/featureFlag'
import { useTranslations } from 'next-intl'
import LocationDetailsModal from '../LocationDetailsModal/LocationDetailsModal'
import { useQueryStateToFormState } from '../CarResults/useQueryToFormState'
import {
  useCarHireSearchFormDates,
  useCarHireSearchForm,
  useCarHireSearchFormLocation,
} from './hooks'
import { CarHireLocationOption } from './SearchCarHireForm.types'
import {
  SearchCarHireLocationDetails,
  SearchCarHireLocationType,
} from './SearchCarHireLocationDetails'
import { SearchFieldSwapButton } from '../SearchFieldSwapButton'
import { MAX_DAYS_RENTAL } from './hooks/useCarHireSearchForm'
import TimePicker from '../TimePicker'

import styles from './SearchCarHireForm.module.scss'

const POPULAR_DESTINATION_CONFIRMATION_MS = 1600

const AutoCompleteOption = ({ children }: React.PropsWithChildren<unknown>) => (
  <div className={styles.autoCompleteOption}>{children}</div>
)

const locationOptions = (
  autocompleteData: CarHireLocationOption,
  autocompleteOptions: LocationOptionProps[],
  labels: Record<string, string>
) => {
  if (autocompleteData.isLoading) {
    return <AutoCompleteOption>{labels.search}</AutoCompleteOption>
  }
  if (autocompleteData.noResultsFound) {
    return (
      <AutoCompleteOption>
        {labels.noResults} <b>"{autocompleteData.searchString}"</b>
      </AutoCompleteOption>
    )
  }
  return autocompleteOptions.map((item, index) => {
    return <AutoCompleteLocationOption key={item.key} item={item} index={index} />
  })
}

const refCallbackFn = (isLargerThanMobile: boolean, autocompleteOptions: CarHireLocationOption) => {
  if (!isLargerThanMobile) {
    return undefined
  }
  return (el: HTMLInputElement | null) => {
    if (el) {
      el.value = autocompleteOptions.searchString
    }
  }
}

const getPickUpLocationDetailsMessage = (
  fullName: string,
  locationDetails: string,
  toggleLocationsDetailModalVisibility: (locationType: SearchCarHireLocationType) => void
) =>
  fullName ? (
    <SearchCarHireLocationDetails
      locationType={SearchCarHireLocationType.PickUpLocation}
      label={locationDetails}
      toggleVisibility={toggleLocationsDetailModalVisibility}
    />
  ) : undefined

const getDropOffLocationDetailsMessage = (
  differentDropOff: boolean,
  fullName: string | undefined,
  locationDetails: string,
  toggleLocationsDetailModalVisibility: (locationType: SearchCarHireLocationType) => void
) =>
  differentDropOff && fullName ? (
    <SearchCarHireLocationDetails
      locationType={SearchCarHireLocationType.DropOffLocation}
      label={locationDetails}
      toggleVisibility={toggleLocationsDetailModalVisibility}
    />
  ) : undefined

const getTimePickerAvailability = (
  multiBrandEnabled: boolean,
  location: CarHireLocation | undefined,
  servingBrandIds: string[],
  allowAfterHoursDropOff = false
) =>
  getServingOpeningTimes(location, multiBrandEnabled ? servingBrandIds : [], allowAfterHoursDropOff)

type SearchCarHireFormProps = {
  buttonText: string
  enablePopularDestinationFlow?: boolean
}

const SearchCarHireForm = React.forwardRef<{ press: () => void }, SearchCarHireFormProps>(
  (props, toggleRef): JSX.Element => {
    const { buttonText, enablePopularDestinationFlow = false } = props
    const { locale } = useLocalisation()
    const [isHydrated, setIsHydrated] = useState(false)
    const [isPopularDestinationConfirmed, setIsPopularDestinationConfirmed] = useState(false)
    const [datePickerControlRequest, setDatePickerControlRequest] = useState<{
      action: 'open'
      requestId: number
    } | null>(null)
    const lastPopularDestinationKeyRef = useRef('')
    const datePickerRequestIdRef = useRef(0)
    const searchFormRef = useRef<HTMLFormElement>(null)
    const popularDestinationConfirmationTimeoutRef = useRef<
      ReturnType<typeof setTimeout> | undefined
    >(undefined)

    useEffect(() => {
      setIsHydrated(true)
    }, [])

    useEffect(
      () => () => {
        clearTimeout(popularDestinationConfirmationTimeoutRef.current)
      },
      []
    )

    const tLabels = useTranslations('pages.search.car_hire.labels')
    const tActions = useTranslations('pages.search.car_hire.actions')
    const tSearch = useTranslations('pages.search.car_hire.search_states')
    const tValidation = useTranslations('pages.search.car_hire.validation')
    const tCommonActions = useTranslations('common.actions')

    const { pathname, query: routerQuery } = useRouter()
    const isReturningFromResults = Boolean(routerQuery.searchType)

    const labels = {
      differentDropOff: tLabels('different_drop_off'),
      driverUnderAgeLimit: tLabels('driver_under_age_limit'),
      dob: tLabels('drivers_dob'),
      pickUpLocation: tLabels('pick_up_location'),
      dropOffLocation: tLabels('drop_off_location'),
      pickUpAndDropOffLocation: tLabels('pick_up_and_drop_off_location'),
      pickUpDate: tLabels('pick_up_date'),
      pickUpTime: tLabels('pick_up_time'),
      dropOffDate: tLabels('drop_off_date'),
      dropOffTime: tLabels('drop_off_time'),
      searchAgain: tCommonActions('search_again'),
      searchCarHire: tActions('search_button'),
      locationDetails: tLabels('location_details'),
      search: tSearch('searching'),
      noResults: tSearch('no_results'),
      avisPreferredMembership: tLabels('avis_preferred_membership'),
      avisPreferredMembershipLabel: tLabels('avis_preferred_membership_number'),
      avisPreferredMembershipHelperInfo: tLabels('avis_preferred_membership_helper'),
    }

    const isLargerThanMobile = useMediaQuery(`(min-width: ${deviceSizes.medium})`)

    const avisPreferredMembership = useFeatureFlag('car-hire-avis-preferred-membership')
    const multiBrandEnabled = useFeatureFlag('car-hire-multi-brand')

    const { query } = useQueryParams<CarHireSearchFormParams>()
    const { formValues: defaultFormValues } = useQueryStateToFormState(query)
    const { minYear, maxYear, minPickUpDate, maxPickUpDate } = useCarHireSearchFormDates()

    const {
      form,
      submitForm,
      isUnderAgeLimit,
      setDateState,
      pickUpLocation,
      dropOffLocation,
      errors,
      updatePickUpDate,
      updateDropOffDate,
      pickUpDate,
      differentDropOff,
      maxDropOffDate,
      dropOffDate,
      isAvisPreferred,
    } = useCarHireSearchForm(defaultFormValues, toggleRef)

    const pickUpAutocomplete = useCarHireSearchFormLocation(
      form,
      defaultFormValues.pickUpSearchString,
      'pickUpLocation',
      'pickUpSearchString'
    )

    const pickUpAutocompleteItems = useMemo(
      () => createAutoCompleteLocationSource(pickUpAutocomplete.locations, true),
      [pickUpAutocomplete.locations]
    )

    const dropOffAutocomplete = useCarHireSearchFormLocation(
      form,
      defaultFormValues.dropOffSearchString,
      'dropOffLocation',
      'dropOffSearchString'
    )
    const dropOffAutocompleteItems = useMemo(
      () => createAutoCompleteLocationSource(dropOffAutocomplete.locations, true),
      [dropOffAutocomplete.locations]
    )
    const clearDropOffLocation = dropOffAutocomplete.clearLocation

    useEffect(() => {
      if (
        !enablePopularDestinationFlow ||
        isReturningFromResults ||
        !defaultFormValues.pickUpLocation.id
      ) {
        return
      }

      const popularDestinationKey = `${defaultFormValues.pickUpLocation.id}:${defaultFormValues.pickUpLocation.type}`
      if (lastPopularDestinationKeyRef.current === popularDestinationKey) {
        return
      }

      lastPopularDestinationKeyRef.current = popularDestinationKey
      form.setValue('differentDropOff', false)
      clearDropOffLocation()
      clearTimeout(popularDestinationConfirmationTimeoutRef.current)
      setIsPopularDestinationConfirmed(true)
      popularDestinationConfirmationTimeoutRef.current = setTimeout(
        () => setIsPopularDestinationConfirmed(false),
        POPULAR_DESTINATION_CONFIRMATION_MS
      )
      searchFormRef.current?.scrollIntoView?.({
        behavior: globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
        block: 'start',
      })
      datePickerRequestIdRef.current += 1
      setDatePickerControlRequest({
        action: 'open',
        requestId: datePickerRequestIdRef.current,
      })
    }, [
      defaultFormValues.pickUpLocation.id,
      defaultFormValues.pickUpLocation.type,
      clearDropOffLocation,
      form,
      enablePopularDestinationFlow,
      isReturningFromResults,
    ])

    const dropOffInputRefCallbackFn = refCallbackFn(isLargerThanMobile, dropOffAutocomplete)
    const pickUpInputRefCallbackFn = refCallbackFn(isLargerThanMobile, pickUpAutocomplete)

    const { control, handleSubmit, formState } = form
    const { touchedFields, isSubmitted } = formState

    const [isLocationDetailsModalVisible, setIsLocationDetailsModalVisible] = useState({
      pickUpLocation: false,
      dropOffLocation: false,
    })

    const toggleLocationsDetailModalVisibility = useCallback(
      (locationType: SearchCarHireLocationType) => {
        setIsLocationDetailsModalVisible({
          ...isLocationDetailsModalVisible,
          [locationType]: !isLocationDetailsModalVisible[locationType],
        })
      },
      [isLocationDetailsModalVisible]
    )

    const pickUpLocationDetailsMessage = getPickUpLocationDetailsMessage(
      pickUpAutocomplete.searchString.trim() && pickUpLocation?.id ? pickUpLocation.fullName : '',
      labels.locationDetails,
      toggleLocationsDetailModalVisibility
    )

    const dropOffLocationDetailsMessage = getDropOffLocationDetailsMessage(
      differentDropOff,
      dropOffAutocomplete.searchString.trim() && dropOffLocation?.id
        ? dropOffLocation.fullName
        : '',
      labels.locationDetails,
      toggleLocationsDetailModalVisibility
    )

    const pushGtaCarSearchAgain = () => {
      refineSearchSessionStorage(pathname)
      return gtmSearchSubmit('car hire')
    }

    const swapLocations = () => {
      if (!dropOffLocation) {
        return
      }
      const [pickUpSearchString, dropOffSearchString] = form.getValues([
        'pickUpSearchString',
        'dropOffSearchString',
      ])

      dropOffAutocomplete?.swapLocations(pickUpSearchString, pickUpLocation)
      pickUpAutocomplete?.swapLocations(dropOffSearchString, dropOffLocation)
    }
    const isSwapDisabled = useMemo(
      () => !pickUpLocation || !dropOffLocation || !differentDropOff,
      [differentDropOff, dropOffLocation, pickUpLocation]
    )

    const isLastAvailableRentalDay = differenceInDays(dropOffDate, pickUpDate) === MAX_DAYS_RENTAL
    const showPickUpTimeError = isSubmitted || !!touchedFields.pickUpTime
    const showDropOffTimeError = isSubmitted || !!touchedFields.dropOffTime
    // Constrain both pickers by the brands that can actually serve this trip. When both brands can,
    // the location's own hours are already the union of the two. When only one can - a dual-brand
    // pick-up dropping off somewhere only Budget serves - the union would offer times that only the
    // unusable brand is open for, and the search would come back empty with nothing to explain it.
    const servingBrandIds = useMemo(
      () =>
        multiBrandEnabled
          ? getCommonBrandIds(pickUpLocation, differentDropOff ? dropOffLocation : undefined)
          : [],
      [differentDropOff, dropOffLocation, multiBrandEnabled, pickUpLocation]
    )
    const pickUpAvailability = getTimePickerAvailability(
      multiBrandEnabled,
      pickUpLocation,
      servingBrandIds
    )
    const dropOffAvailability =
      dropOffLocation?.openingTimes?.length && differentDropOff
        ? getTimePickerAvailability(multiBrandEnabled, dropOffLocation, servingBrandIds, true)
        : getTimePickerAvailability(multiBrandEnabled, pickUpLocation, servingBrandIds, true)
    const dropOffLocationForValidation = differentDropOff ? dropOffLocation : pickUpLocation
    const dropOffDateAvailability = getTimePickerAvailability(
      multiBrandEnabled,
      dropOffLocationForValidation,
      servingBrandIds,
      true
    )
    const isPickUpDateClosed = isClosedOnDate(pickUpAvailability, pickUpDate)
    const isDropOffDateClosed = isClosedOnDate(dropOffDateAvailability, dropOffDate)
    const pickUpTimeHelperMessage =
      pickUpLocation?.id && !isPickUpDateClosed ? tValidation('time_not_available') : undefined
    const dropOffTimeHelperMessage =
      dropOffLocationForValidation?.id && !isDropOffDateClosed
        ? tValidation('time_not_available')
        : undefined

    return (
      <form
        ref={searchFormRef}
        autoComplete="off"
        onSubmit={handleSubmit(submitForm)}
        className={styles.form}
      >
        {isLocationDetailsModalVisible.pickUpLocation && pickUpLocation && (
          <LocationDetailsModal
            location={pickUpLocation}
            onClose={() =>
              toggleLocationsDetailModalVisibility(SearchCarHireLocationType.PickUpLocation)
            }
            isOpen={isLocationDetailsModalVisible.pickUpLocation}
          />
        )}
        {isLocationDetailsModalVisible.dropOffLocation && dropOffLocation && (
          <LocationDetailsModal
            location={dropOffLocation}
            onClose={() =>
              toggleLocationsDetailModalVisibility(SearchCarHireLocationType.DropOffLocation)
            }
            isOpen={isLocationDetailsModalVisible.dropOffLocation}
          />
        )}

        <Box
          className={classNames(
            styles.row1,
            (pickUpLocationDetailsMessage || dropOffLocationDetailsMessage) && styles.extraGap
          )}
        >
          <Controller
            control={control}
            name="pickUpSearchString"
            render={() => (
              <div
                className={classNames(
                  styles.locationInputWrapper,
                  pickUpLocationDetailsMessage && styles.extraMarginBottom
                )}
                data-popular-destination-confirmation={
                  isPopularDestinationConfirmed ? 'true' : undefined
                }
              >
                <AutoCompleteWithMobileModal
                  name="pickUpLocation"
                  label={differentDropOff ? labels.pickUpLocation : labels.pickUpAndDropOffLocation}
                  accessibleLabel={labels.pickUpLocation}
                  value={pickUpAutocomplete.searchString}
                  items={pickUpAutocompleteItems}
                  onInputFocus={pickUpAutocomplete.onInputFocus}
                  onInputValueChange={(val) => {
                    pickUpAutocomplete.handleAutocompleteChange(val.inputValue)
                  }}
                  onSelectedItemChange={(v) => {
                    pickUpAutocomplete.handleSuggestionClick(v.selectedItem?.value || '')
                    dropOffAutocomplete.clearLocation()
                  }}
                  error={errors.pickUpSearchString?.message || ''}
                  helperMessage={pickUpLocationDetailsMessage}
                  ref={pickUpInputRefCallbackFn}
                  endIconProps={
                    isPopularDestinationConfirmed
                      ? { iconName: 'circle-check', iconSize: '1x', color: 'successPrimary' }
                      : undefined
                  }
                >
                  {locationOptions(pickUpAutocomplete, pickUpAutocompleteItems, labels)}
                </AutoCompleteWithMobileModal>
              </div>
            )}
          />
          {differentDropOff && (
            <>
              <Box className={styles.swapButtonWrapper}>
                <SearchFieldSwapButton
                  onClick={swapLocations}
                  disabled={isSwapDisabled}
                  label="Swap car hire pick up and drop off locations"
                />
              </Box>
              <Controller
                control={control}
                name="dropOffSearchString"
                render={() => (
                  <div
                    className={classNames(
                      styles.locationInputWrapper,
                      dropOffLocationDetailsMessage && styles.extraMarginBottom
                    )}
                  >
                    <AutoCompleteWithMobileModal
                      name="dropOffLocation"
                      label={labels.dropOffLocation}
                      value={dropOffAutocomplete.searchString}
                      items={dropOffAutocompleteItems}
                      onInputFocus={dropOffAutocomplete.onInputFocus}
                      onInputValueChange={(val) => {
                        dropOffAutocomplete.handleAutocompleteChange(val.inputValue)
                      }}
                      onSelectedItemChange={(v) => {
                        dropOffAutocomplete.handleSuggestionClick(v.selectedItem?.value || '')
                      }}
                      error={errors.dropOffSearchString?.message || ''}
                      disabled={!differentDropOff}
                      helperMessage={dropOffLocationDetailsMessage}
                      ref={dropOffInputRefCallbackFn}
                    >
                      {locationOptions(dropOffAutocomplete, dropOffAutocompleteItems, labels)}
                    </AutoCompleteWithMobileModal>
                  </div>
                )}
              />
            </>
          )}

          <Controller
            control={control}
            name="differentDropOff"
            render={({ field: { onChange, value, name } }) => {
              return (
                <Box className={styles.differentDropOff}>
                  <Checkbox
                    id="differentDropOff"
                    name={name}
                    value={value.toString()}
                    isSelected={value}
                    onChange={() => {
                      dropOffAutocomplete.clearLocation()
                      return onChange(!value)
                    }}
                  >
                    {labels.differentDropOff}
                  </Checkbox>
                </Box>
              )
            }}
          />
        </Box>

        <Box className={styles.row2}>
          <Controller
            control={control}
            name="pickUpDate"
            render={({ field: { value } }) => (
              <DatePicker
                startLabel={labels.pickUpDate}
                minDate={minPickUpDate}
                maxDate={maxPickUpDate}
                values={{ startDateTime: value }}
                onChange={setDateState(updatePickUpDate)}
                errorStartDate={errors.pickUpDate?.message}
                locale={locale}
                filterDate={(date) => !isClosedOnDate(pickUpAvailability, date)}
                controlRequest={datePickerControlRequest}
              />
            )}
          />

          <Controller
            control={control}
            name="pickUpTime"
            render={({ field: { onChange, value } }) => (
              <TimePicker
                label={labels.pickUpTime}
                value={value}
                setTime={(newValue) => {
                  onChange(newValue)
                }}
                errors={
                  showPickUpTimeError && errors.pickUpTime?.message
                    ? [errors.pickUpTime.message]
                    : undefined
                }
                availability={pickUpAvailability}
                helperMessage={pickUpTimeHelperMessage}
                chosenDate={pickUpDate}
                locale={locale}
              />
            )}
          />

          <Controller
            control={control}
            name="dropOffDate"
            render={({ field: { value } }) => (
              <DatePicker
                name="dropOffDate"
                startLabel={labels.dropOffDate}
                minDate={pickUpDate}
                maxDate={maxDropOffDate}
                values={{ startDateTime: value }}
                onChange={setDateState(updateDropOffDate)}
                errorStartDate={errors.dropOffDate?.message}
                locale={locale}
                filterDate={(date) => !isClosedOnDate(dropOffDateAvailability, date)}
              />
            )}
          />

          <Controller
            control={control}
            name="dropOffTime"
            render={({ field: { onChange, value } }) => (
              <TimePicker
                label={labels.dropOffTime}
                value={value}
                setTime={(newValue) => {
                  onChange(newValue)
                }}
                errors={
                  showDropOffTimeError && errors.dropOffTime?.message
                    ? [errors.dropOffTime.message]
                    : undefined
                }
                availability={dropOffAvailability}
                helperMessage={dropOffTimeHelperMessage}
                minDateTime={getDateTime(form.getValues('pickUpTime'), pickUpDate)}
                chosenDate={dropOffDate}
                locale={locale}
                isLastAvailableRentalDay={isLastAvailableRentalDay}
              />
            )}
          />

          <Button
            type="submit"
            onPress={pushGtaCarSearchAgain}
            iconStartProps={isHydrated ? { iconName: 'search', iconPrefix: 'fas' } : undefined}
            size={isLargerThanMobile ? 'lg' : 'md'}
          >
            {buttonText}
          </Button>
        </Box>

        <hr className={styles.divider} />

        <Box className={styles.row3}>
          <Controller
            control={control}
            name="isUnderAgeLimit"
            render={({ field: { onChange, value, name } }) => (
              <Box className={styles.flexAuto}>
                <Checkbox
                  id="isUnderAgeLimit"
                  name={name}
                  value={value.toString()}
                  isSelected={value}
                  onChange={onChange}
                >
                  {labels.driverUnderAgeLimit}
                </Checkbox>
              </Box>
            )}
          />

          {isUnderAgeLimit && (
            <Controller
              control={control}
              name="dob"
              render={({ field: { onChange, value } }) => (
                <Box className={styles.flexAuto}>
                  <DatePicker
                    name="dob"
                    startLabel={labels.dob}
                    monthsShown={1}
                    showYearDropdown
                    dropdownMode="select"
                    minDate={minYear}
                    maxDate={maxYear}
                    values={{ startDateTime: value }}
                    onChange={setDateState(onChange)}
                    locale={locale}
                  />
                </Box>
              )}
            />
          )}

          {avisPreferredMembership && (
            <Controller
              control={control}
              name="isAvisPreferred"
              render={({ field: { onChange, value, name } }) => (
                <Box flexDirection="row" alignItems="center" gap="3xs" className={styles.flexAuto}>
                  <Checkbox
                    id="isAvisPreferred"
                    name={name}
                    value={value.toString()}
                    isSelected={value}
                    onChange={onChange}
                  >
                    {labels.avisPreferredMembership}
                  </Checkbox>
                  <span className={styles.avisMembershipTooltip}>
                    <Tooltip
                      content={<p>{labels.avisPreferredMembershipHelperInfo}</p>}
                      iconProps={
                        isHydrated
                          ? {
                              iconName: 'circle-info',
                              iconSize: '1x',
                            }
                          : undefined
                      }
                      closeButtonText="OK"
                    />
                  </span>
                </Box>
              )}
            />
          )}

          {isAvisPreferred && avisPreferredMembership && (
            <Controller
              control={control}
              name="membershipCode"
              render={({ field: { onChange, value } }) => (
                <TextField
                  name="membershipCode"
                  label={labels.avisPreferredMembershipLabel}
                  value={value}
                  errorMessage={errors.membershipCode?.message ?? undefined}
                  isRequired
                  onInput={(e) => {
                    onChange(e.currentTarget.value)
                  }}
                  maxLength={6}
                />
              )}
            />
          )}
        </Box>
      </form>
    )
  }
)

export default SearchCarHireForm
